import React, { useState } from 'react';
import { Check, X, Phone, Clock } from 'lucide-react';
import type { Inquiry, Agent } from '../../types/cms';

interface OwnerListingApprovalsProps {
  inquiries: Inquiry[];
  token: string;
  apiBase: string;
  agents: Agent[];
  onApprove: (inquiry: Inquiry) => void;
  onRefresh: () => void;
  showMessage: (msg: string) => void;
}

const statusLabels: Record<string, string> = {
  new: '待處理',
  contacted: '聯絡中',
  closed: '已結束',
  approved: '已批准',
  rejected: '已拒絕',
};

const statusClasses: Record<string, string> = {
  new: 'bg-red-100 text-red-700',
  contacted: 'bg-gold-100 text-gold-700',
  closed: 'bg-slate-100 text-slate-600',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-slate-200 text-slate-600',
};

export const OwnerListingApprovals: React.FC<OwnerListingApprovalsProps> = ({
  inquiries,
  token,
  apiBase,
  agents,
  onApprove,
  onRefresh,
  showMessage,
}) => {
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const ownerListings = inquiries.filter((inq) => inq.type === 'owner_listing');

  const updateStatus = async (id: string, status: Inquiry['status'], rejectReason?: string) => {
    try {
      const res = await fetch(`${apiBase}/api/inquiries/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status, rejectReason }),
      });
      if (res.ok) {
        showMessage('狀態已更新');
        onRefresh();
      } else {
        showMessage('更新失敗');
      }
    } catch {
      showMessage('更新失敗');
    }
  };

  const handleReject = (id: string) => {
    updateStatus(id, 'rejected', reason || undefined);
    setRejectId(null);
    setReason('');
  };

  return (
    <div className="space-y-6">
      {ownerListings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
          <Clock className="mx-auto mb-4 h-10 w-10 text-slate-300" />
          <h4 className="mb-2 text-xl font-bold text-slate-800">暫無業主放盤申請</h4>
          <p className="text-sm text-slate-500">有新的業主放盤時會在這裡顯示。</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left">時間</th>
                <th className="px-4 py-3 text-left">聯絡人</th>
                <th className="px-4 py-3 text-left">電話</th>
                <th className="px-4 py-3 text-left">物業類型</th>
                <th className="px-4 py-3 text-left">放盤意向</th>
                <th className="px-4 py-3 text-left">地址 / 面積 / 期望價</th>
                <th className="px-4 py-3 text-left">狀態</th>
                <th className="px-4 py-3 text-left">操作</th>
              </tr>
            </thead>
            <tbody>
              {ownerListings.map((inq) => (
                <tr key={inq.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(inq.createdAt).toLocaleString('zh-HK')}</td>
                  <td className="px-4 py-3 font-semibold">{inq.name}</td>
                  <td className="px-4 py-3">
                    <a href={`tel:${inq.phone}`} className="text-blue-800 hover:underline">
                      {inq.phone}
                    </a>
                  </td>
                  <td className="px-4 py-3">{inq.propertyType}</td>
                  <td className="px-4 py-3">{inq.listingType}</td>
                  <td className="px-4 py-3 max-w-xs">
                    <p className="truncate" title={inq.address}>{inq.address || '-'}</p>
                    <p className="text-xs text-slate-500">{inq.area ? `面積：${inq.area}` : ''}{inq.expectedPrice ? ` / 期望價：${inq.expectedPrice}` : ''}</p>
                    {inq.message && <p className="mt-1 text-xs text-slate-500">{inq.message}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded px-2 py-1 text-xs font-bold ${statusClasses[inq.status] || 'bg-slate-100'}`}>
                      {statusLabels[inq.status] || inq.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {inq.status !== 'approved' && inq.status !== 'rejected' && (
                        <>
                          <button
                            onClick={() => onApprove(inq)}
                            className="flex items-center gap-1 rounded bg-green-700 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-green-800"
                          >
                            <Check className="h-3.5 w-3.5" />
                            批准並轉為物業
                          </button>
                          <button
                            onClick={() => updateStatus(inq.id, 'contacted')}
                            className="flex items-center gap-1 rounded bg-gold-600 px-2.5 py-1.5 text-xs font-bold text-white hover:bg-gold-700"
                          >
                            <Phone className="h-3.5 w-3.5" />
                            聯絡中
                          </button>
                          <button
                            onClick={() => setRejectId(inq.id)}
                            className="flex items-center gap-1 rounded bg-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-300"
                          >
                            <X className="h-3.5 w-3.5" />
                            拒絕
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejectId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 text-lg font-bold text-slate-900">拒絕業主放盤</h3>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="可選填拒絕原因（不會發送給業主，僅後台記錄）"
              rows={4}
              className="mb-4 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setRejectId(null)} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
                取消
              </button>
              <button onClick={() => handleReject(rejectId)} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">
                確認拒絕
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
